<script setup lang="ts">
import type { PageCollectionItemBase } from '@nuxt/content'

const props = defineProps<{
  post: PageCollectionItemBase
  horizontal?: boolean
}>()

const readingTime = computed(() => articleReadingTime(articleBodyToString(props.post)))
</script>

<template>
  <v-card
    :to="post.path"
    :aria-label="post.title"
    class="article-card"
    :class="{ 'article-card--horizontal': horizontal }"
  >
    <ArticleImage
      :src="post.meta?.image"
      alt=""
      :seed="post.title"
      :base-color="articleCategoryBg(post.meta?.category)"
      :variant="articleCategoryVariant(post.meta?.category)"
      :fetch-width="600"
      :fetch-height="340"
      aspect-ratio="16 / 9"
      class="article-card__image"
    />
    <div class="article-card__content">
      <div class="article-card__meta">
        <v-chip
          v-if="post.meta?.category"
          :color="articleCategoryColor(post.meta.category)"
          size="small"
          variant="tonal"
          >{{ post.meta.category }}</v-chip
        >
        <span>{{ readingTime }}</span>
      </div>
      <h3>{{ post.title }}</h3>
      <p>{{ post.description }}</p>
      <span v-if="!horizontal" class="article-card__link"> Read article <v-icon icon="$arrowRight" size="18" /> </span>
    </div>
  </v-card>
</template>

<style scoped>
.article-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
}
.article-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 24px;
  min-width: 0;
}
.article-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}
.article-card__meta :deep(.v-chip) {
  height: auto;
  min-height: 24px;
}
.article-card__meta :deep(.v-chip__content) {
  white-space: normal;
  padding-block: 4px;
  color: rgb(var(--v-theme-on-surface));
}
.article-card h3 {
  font-size: 1.25rem;
  line-height: 1.4;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.article-card p {
  color: rgba(var(--v-theme-on-surface), 0.75);
  line-height: 1.6;
}
.article-card__link {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}
.article-card--horizontal {
  flex-direction: row;
  align-items: flex-start;
  padding: 20px;
  gap: 20px;
}
.article-card--horizontal .article-card__image {
  width: 160px !important;
  flex-shrink: 0;
  border-radius: 12px;
}
.article-card--horizontal .article-card__content {
  padding: 0;
}
.article-card--horizontal h3 {
  font-size: 1.125rem;
}
.article-card--horizontal p {
  font-size: 0.9rem;
}
@media (max-width: 600px) {
  .article-card__content {
    padding: 20px;
  }
  .article-card--horizontal {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    padding: 16px;
    gap: 12px;
  }
  .article-card--horizontal .article-card__content {
    display: contents;
  }
  .article-card--horizontal .article-card__meta {
    align-self: center;
  }
  .article-card--horizontal h3,
  .article-card--horizontal p {
    grid-column: 1 / -1;
  }
  .article-card--horizontal .article-card__image {
    width: 64px !important;
    aspect-ratio: 1 !important;
  }
}
</style>
